import {
    AirlineSeatReclineNormal,
    AirportShuttle,
    ChildCare,
    GridView,
    LocalBar,
    Mosque,
    OndemandVideo,
    PowerSettingsNew,
    Restaurant,
    Shower,
    SignalCellularAlt,
    Wifi
} from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'

export interface TooltipAmenityChild {
    key: string
    displayText: string
}

export interface TooltipAmenityItem {
    key: string
    displayText: string
    children: TooltipAmenityChild[]
}

export interface AmenitiesDetailListProps {
    items: TooltipAmenityItem[]
    amenitiesRemarksNote?: string
    listSx?: object
}

const AMENITY_ICON_MAP = {
    seatType: AirlineSeatReclineNormal,
    seatLayout: GridView,
    meals: Restaurant,
    beverages: LocalBar,
    wifi: Wifi,
    powerType: PowerSettingsNew,
    avod: OndemandVideo,
    infantCare: ChildCare,
    mobile: SignalCellularAlt,
    prayerArea: Mosque,
    provideTransportation: AirportShuttle,
    showerArea: Shower
} as const

const AmenitiesDetailList = ({ items, amenitiesRemarksNote, listSx }: AmenitiesDetailListProps) => {
    const t = useTranslations('srpFlightDetails')

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                py: 0.25,
                ...listSx
            }}
        >
            {items.map(item => {
                const IconComponent = AMENITY_ICON_MAP[item.key as keyof typeof AMENITY_ICON_MAP]
                if (!IconComponent) return null

                return (
                    <Box key={item.key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconComponent sx={{ fontSize: 16, color: { xs: 'text.primary', lg: 'common.white' }, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 400, color: { xs: 'text.primary', lg: 'common.white' }, lineHeight: 1.4 }}>
                                {item.displayText}
                            </Typography>
                        </Box>
                        {item.children.length > 0 && (
                            <Box sx={{ pl: 3, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {item.children.map(child => (
                                    <Typography
                                        key={child.key}
                                        sx={{ fontSize: 12, fontWeight: 400, color: { xs: 'text.primary', lg: 'common.white' }, lineHeight: 1.35 }}
                                    >
                                        {child.displayText}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                )
            })}
            {amenitiesRemarksNote ? (
                <Typography
                    sx={{
                        mt: 1,
                        fontSize: 12,
                        fontWeight: 400,
                        color: { xs: 'text.primary', lg: 'common.white' },
                        lineHeight: 1.35
                    }}
                >
                    {t('aircraftAmenitiesRemarksNote', { text: amenitiesRemarksNote })}
                </Typography>
            ) : null}
        </Box>
    )
}

export default AmenitiesDetailList
